import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';

// COMPONENTS
import Logo from './logoComponent';
import SearchBar from './searchBarComponent';
import Menu from './menuComponent';
import Profile from './profileComponent';
import TooltipComponent from './tooltipComponent';

// API
import * as users from 'api/userApi';

// CONTEXT
import { useMenu } from 'context/MenuContext';

// SERVICES
import { insertSearchStat } from 'services/statService';
import { getTilesHasNew10D } from 'services/tileService';
import { getCustomButton } from 'services/settingService';

// HOOKS
import { usePendingProposals } from 'hooks/usePendingProposals';
import { useTheme } from 'context/ThemeContext';

// ICONS
import { Croissant, Menu as MenuIcon, SquarePen, UserRoundCog, Sun, Moon, HelpCircle } from 'lucide-react';

// Icône du bouton personnalisé : chargée à la demande (chunk séparé) pour ne
// pas embarquer toute la librairie lucide-react dans le bundle initial.
const LucideCustomIcon = lazy(() => import('./LucideCustomIcon'));

const Header = (user) => {
    const navigate = useNavigate();
    const { pendingProposals } = useMenu();
    const { theme, changeTheme } = useTheme();
    const [isAdmin, setIsAdmin] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchEngine, setSearchEngine] = useState('Google');
    const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
    const [hasNewTiles, setHasNewTiles] = useState(false);
    const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
    const [customButtonUrl, setCustomButtonUrl] = useState('');
    const [customButtonIcon, setCustomButtonIcon] = useState('HelpCircle');
    const [customButtonTooltip, setCustomButtonTooltip] = useState('');

    // Hook pour gérer les propositions en attente
    usePendingProposals(isAdmin);

    useEffect(() => {
        if (user?.user?.userBDDInfo.data[0].USR_IS_ADMIN !== undefined) {
            setIsAdmin(user.user.userBDDInfo.data[0].USR_IS_ADMIN);
        }
        if (user?.user?.userBDDInfo.data[0].USR_BROWSER !== undefined) {
            setSearchEngine(user.user.userBDDInfo.data[0].USR_BROWSER);
        }
    }, [user]);

    const email = user?.user?.userBDDInfo.data[0].USR_EMAILADDRESS || '';
    const defaultBrowser = user?.user?.userBDDInfo.data[0].USR_BROWSER || 'Google';
    const fullName = user?.user?.userADInfo.data.ad_ai.uaiad_displayname;

    useEffect(() => {
        setSearchEngine(defaultBrowser);
    }, [defaultBrowser]);

    // Fonction pour vérifier les nouvelles tuiles disponibles
    const checkNewTiles = async () => {
        try {
            const response = await getTilesHasNew10D();

            // Nouveau comportement :
            // - on privilégie le flag HAS_NEW_OPTIONAL_TILES_10D renvoyé par l'API
            // - fallback sur HAS_NEW_TILES_10D si jamais le premier n'existe pas (compatibilité)
            const data = response?.data?.[0] || {};
            const hasNewOptional = data.HAS_NEW_OPTIONAL_TILES_10D;
            const hasAnyNew = data.HAS_NEW_TILES_10D;

            setHasNewTiles(Boolean(
                typeof hasNewOptional !== 'undefined'
                    ? hasNewOptional
                    : hasAnyNew
            ));
        } catch (error) {
            console.error('Erreur lors de la vérification des nouvelles tuiles:', error);
            setHasNewTiles(false);
        }
    };

    // Vérifier les nouvelles tuiles au chargement et périodiquement
    useEffect(() => {
        checkNewTiles();
        
        // Vérifier toutes les 5 minutes (plus raisonnable)
        const interval = setInterval(() => {
            checkNewTiles();
        }, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    // Charger les paramètres du bouton personnalisé
    useEffect(() => {
        const loadCustomButton = async () => {
            try {
                const { enabled, url, icon, tooltip } = await getCustomButton();
                setCustomButtonEnabled(!!enabled);
                setCustomButtonUrl(url || '');
                setCustomButtonIcon(icon || 'HelpCircle');
                setCustomButtonTooltip(tooltip || '');
            } catch (error) {
                console.error('Erreur lors de la récupération du bouton personnalisé :', error);
                setCustomButtonEnabled(false);
                setCustomButtonUrl('');
                setCustomButtonIcon('HelpCircle');
                setCustomButtonTooltip('');
            }
        };
        loadCustomButton();
    }, []);

    const clearSearchBar = () => setSearchTerm('');

    const handleSearch = () => {
        if (!searchTerm) return;

        const searchUrls = {
            Google: `https://www.google.com/search?q=${encodeURIComponent(searchTerm)}`,
            Bing: `https://www.bing.com/search?q=${encodeURIComponent(searchTerm)}`,
            QWant: `https://www.qwant.com/?q=${encodeURIComponent(searchTerm)}`,
            Yahoo: `https://search.yahoo.com/search?p=${encodeURIComponent(searchTerm)}`,
            DuckDuckGo: `https://duckduckgo.com/?q=${encodeURIComponent(searchTerm)}`,
            Ecosia: `https://www.ecosia.org/search?q=${encodeURIComponent(searchTerm)}`,
        };

        window.open(searchUrls[searchEngine], '_blank');
        if (user.user.userBDDInfo.data[0].USR_ID) {
            insertSearchStat(user.user.userBDDInfo.data[0].USR_ID, searchEngine);
        }
        clearSearchBar();
    };
    
    const handleEditInterface = () => {
        navigate('/edit-interface');
    };

    const handleGoAdmin = () => {
        navigate('/admin/dashboard/analytics');
    };

    const updateUser = async (fieldsToUpdate) => {
        try {
            await users.updateUser(fieldsToUpdate);
        } catch (error) {
            console.error('Erreur lors de la mise à jour du navigateur :', error);
        }
    };

    const handleSearchEngineChange = (engine) => {
        setSearchEngine(engine);
        if (email) {
            updateUser({ browser: engine });
        }
        setIsSearchMenuOpen(false);
    };

    const handleLogoClick = () => {
        window.open('https://www.altitudeinfra.fr/', '_blank');
    };

    const handleCroissantClick = async () => {
        window.open('https://croissants.altitudeinfra.fr/', '_blank', 'noopener,noreferrer');
    };

    const handleCustomButtonClick = () => {
        if (!customButtonUrl) return;
        window.open(customButtonUrl, '_blank', 'noopener,noreferrer');
    };

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Items du menu burger (icône + titre)
    const mobileMenuItems = [
        { id: 'theme', text: theme === 'light' ? 'Mode Nuit' : 'Mode Jour', icon: theme === 'light' ? Moon : Sun, onClick: () => changeTheme(theme === 'light' ? 'dark' : 'light') },
        { id: 'edit', text: "Modifier l'interface", icon: SquarePen, onClick: handleEditInterface },
        isAdmin && { id: 'admin', text: 'Administration', icon: UserRoundCog, onClick: handleGoAdmin },
    ].filter(Boolean);

    return (
        <header className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-blanc dark:bg-noir relative z-50 overflow-visible">
            <div className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                {/* Ligne supérieure (mobile) / Colonne gauche (desktop) */}
                <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="flex items-center justify-start flex-shrink-0">
                        <Logo handleLogoClick={handleLogoClick} />
                    </div>
                    {/* Boutons côté droit en mobile */}
                    <div className="flex items-center gap-2 sm:hidden">
                        <TooltipComponent title="Croissants Altitude">
                            <button
                                type="button"
                                onClick={handleCroissantClick}
                                aria-label="Croissants Altitude"
                                className={`p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none`}
                            >
                                <Croissant size={20} />
                            </button>
                        </TooltipComponent>
                        {/* Bouton Burger */}
                        <button
                            type="button"
                            aria-label="Ouvrir le menu"
                            className={`p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none`}
                            onClick={() => setIsMobileMenuOpen(v => !v)}
                        >
                            <MenuIcon size={20} />
                        </button>
                    </div>
                </div>

                {/* Barre de recherche: centrée entre le logo et le croissant */}
                <div className="hidden sm:flex sm:flex-1 sm:justify-center sm:items-center min-w-0 overflow-visible">
                    <div className="w-full max-w-[600px] min-w-0">
                        <SearchBar
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            searchEngine={searchEngine}
                            setSearchEngine={setSearchEngine}
                            handleSearch={handleSearch}
                            clearSearchBar={clearSearchBar}
                            handleSearchEngineChange={handleSearchEngineChange}
                            isSearchMenuOpen={isSearchMenuOpen}
                            setIsSearchMenuOpen={setIsSearchMenuOpen}
                        />
                    </div>
                </div>

                {/* Boutons côté droit sur desktop */}
                <div className="hidden sm:flex items-center justify-end gap-2 flex-shrink-0">
                    {customButtonEnabled && customButtonUrl && (
                        <>
                            <TooltipComponent title={customButtonTooltip}>
                                <button
                                    type="button"
                                    onClick={handleCustomButtonClick}
                                    aria-label={customButtonTooltip}
                                    className={`p-2 rounded-md text-bleu dark:text-blanc hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none`}
                                >
                                    <Suspense fallback={<HelpCircle size={20} />}>
                                        <LucideCustomIcon name={customButtonIcon} size={20} />
                                    </Suspense>
                                </button>
                            </TooltipComponent>
                            <div className={`h-6 border-l border-bleu/30 dark:border-blanc/30 mx-1 sm:mx-2`} />
                        </>
                    )}
                    <TooltipComponent title="Croissants Altitude">
                        <button
                            type="button"
                            onClick={handleCroissantClick}
                            aria-label="Croissants Altitude"
                            className={`p-2 rounded-md text-bleu dark:text-blanc hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none`}
                        >
                            <Croissant size={20} />
                        </button>
                    </TooltipComponent>
                    <div className={`h-6 border-l border-bleu/30 dark:border-blanc/30 mx-1 sm:mx-2`} />
                    <Menu
                        onModifyInterface={handleEditInterface}
                        isAdmin={isAdmin}
                        hasNewTiles={hasNewTiles}
                        hasPendingProposals={pendingProposals > 0}
                    />
                    {email && (
                        <Profile fullName={fullName} email={email} />
                    )}
                </div>
            </div>

            {/* Dropdown mobile sous le header */}
            {isMobileMenuOpen && (
                <div className={`sm:hidden mt-2 w-full rounded-lg shadow-lg overflow-hidden bg-blanc dark:bg-dark-secondary border border-gray-200 dark:border-gray-700`}>
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {mobileMenuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        onClick={() => { item.onClick(); setIsMobileMenuOpen(false); }}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none`}
                                    >
                                        {Icon && <Icon size={20} />}
                                        <span className="text-sm font-medium">{item.text}</span>
                                    </button>
                                </li>
                            );
                        })}
                        {email && (
                            <li>
                                <div className="w-full flex items-center justify-between px-4 py-3">
                                    <span className={`text-sm font-medium text-gray-800 dark:text-gray-200`}>Profil</span>
                                    <Profile fullName={fullName} email={email} />
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </header>
    );
};

export default Header;